import { z } from 'zod';
import { deviceSummarySchema } from './device.js';
import { siteSchema } from './site.js';

/**
 * WebSocket protocol. Clients subscribe to topics; the server answers with a
 * snapshot and then streams patches. Topics are authorised on subscribe against
 * the session's org, so a client cannot listen to another tenant's stream.
 *
 *   devices:<orgId>   device rows for one org ("*" for superadmin, all orgs)
 *   device:<deviceId> one device's detail changes
 *   sites:<orgId>     gateway status
 *   jobs:<orgId>      background job progress
 */
export const topicSchema = z
  .string()
  .regex(/^(devices|device|sites|jobs):[A-Za-z0-9_*-]+$/, 'unknown topic');
export type Topic = z.infer<typeof topicSchema>;

export const clientMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('subscribe'), topics: z.array(topicSchema).min(1).max(20) }),
  z.object({ type: z.literal('unsubscribe'), topics: z.array(topicSchema).min(1).max(20) }),
  z.object({ type: z.literal('ping') }),
]);
export type ClientMessage = z.infer<typeof clientMessageSchema>;

export const serverMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('pong') }),
  z.object({ type: z.literal('subscribed'), topics: z.array(z.string()) }),
  z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),
  z.object({
    type: z.literal('devices.snapshot'),
    topic: z.string(),
    at: z.number().int(),
    devices: z.array(deviceSummarySchema),
  }),
  z.object({
    type: z.literal('devices.patch'),
    topic: z.string(),
    at: z.number().int(),
    upserted: z.array(deviceSummarySchema),
    removed: z.array(z.string()),
  }),
  z.object({
    type: z.literal('sites.patch'),
    topic: z.string(),
    at: z.number().int(),
    upserted: z.array(siteSchema),
    removed: z.array(z.string()),
  }),
  z.object({
    type: z.literal('job.update'),
    topic: z.string(),
    at: z.number().int(),
    job: z.object({
      id: z.string(),
      kind: z.string(),
      status: z.enum(['queued', 'running', 'done', 'failed']),
      progress: z.number().min(0).max(1).nullable(),
      message: z.string().nullable(),
    }),
  }),
]);
export type ServerMessage = z.infer<typeof serverMessageSchema>;

export function devicesTopic(orgId: string | null): Topic {
  return `devices:${orgId ?? '*'}`;
}
export function deviceTopic(deviceId: string): Topic {
  return `device:${deviceId}`;
}
export function sitesTopic(orgId: string | null): Topic {
  return `sites:${orgId ?? '*'}`;
}
export function jobsTopic(orgId: string | null): Topic {
  return `jobs:${orgId ?? '*'}`;
}
